import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import {
	confirm,
	type OverlayBeforeCloseDetail,
} from "@mhmo91/schmancy/overlay";
import { css, html, type PropertyValues } from "lit";
import { property, state } from "lit/decorators.js";
import { EMPTY, fromEvent, merge, takeUntil, tap } from "rxjs";
import { LocalizeMixin } from "../i18n/localize.ts";

@schmancyElement("integration-configuration")
export class IntegrationConfiguration extends LocalizeMixin(SchmancyElement) {
	static styles = [css`:host {display:contents}`];
	@property({ type: Boolean }) saving = false;
	@property({ type: Boolean }) succeeded = false;
	@state() private dirty = false;
	private attempted = false;
	private closing = false;
	override connectedCallback(): void {
		super.connectedCallback();
		const root = this.getRootNode();
		merge(
			fromEvent(this, "input").pipe(
				tap(() => {
					this.dirty = true;
				}),
			),
			fromEvent(this, "change").pipe(
				tap(() => {
					this.dirty = true;
				}),
			),
			fromEvent(this, "close", { capture: true }).pipe(
				tap((event) => {
					if (event.target === this || (!this.dirty && !this.saving)) return;
					event.stopPropagation();
					void this.close();
				}),
			),
			root instanceof ShadowRoot
				? fromEvent<CustomEvent<OverlayBeforeCloseDetail>>(
						root.host,
						"beforeclose",
					).pipe(
						tap((event) => {
							if (!this.dirty && !this.saving) return;
							event.preventDefault();
							if (event.detail.reason === "structured") void this.close();
						}),
					)
				: EMPTY,
		)
			.pipe(takeUntil(this.disconnecting))
			.subscribe();
	}
	protected override updated(changed: PropertyValues): void {
		super.updated(changed);
		if (changed.has("saving") && this.saving) this.attempted = true;
		if (
			this.attempted &&
			!this.saving &&
			(changed.has("saving") || changed.has("succeeded"))
		) {
			if (this.succeeded) this.dirty = false;
			this.attempted = false;
		}
	}
	private async close(): Promise<void> {
		if (this.saving || this.closing) return;
		if (this.dirty) {
			this.closing = true;
			const discard = await confirm({
				title: this.t("admin.integrations.metaAds.form.wizard.discardTitle"),
				confirmText: this.t(
					"admin.integrations.metaAds.form.wizard.discardButton",
				),
				cancelText: this.t(
					"admin.integrations.metaAds.form.wizard.keepEditing",
				),
				variant: "danger",
			});
			this.closing = false;
			if (!discard || !this.isConnected) return;
		}
		this.dispatchEvent(
			new CustomEvent("close", { bubbles: true, composed: true }),
		);
	}
	override render() {
		return html`<slot></slot>`;
	}
}
declare global {
	interface HTMLElementTagNameMap {
		"integration-configuration": IntegrationConfiguration;
	}
}
